import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Modal,
  Button,
  Typography,
  CircularProgress,
} from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import Styles from "./BusinessDescriptionForm.module.css";
import ProgressBar from "../ProgressBar";
import FormFooter from "../Footer/FormFooter";
import SIPOCDiagramStep from "./FormSteps/SIPOCDiagramStep";
import JobHierarchyUploadStep from "./FormSteps/JobHierarchyUploadStep";
import KPIBasket, { KPIBasketData } from "./FormSteps/KPIBasket";
import CriticalTableStep from "./FormSteps/CriticalTableStep";
import FinalStatus from "./FormSteps/FinalStatus";

import { useApiService } from "../../Reusable/utils/baseApiService";

// import { fetchBusinessDescriptionDetails } from "../../Reusable/utils/baseApiService";
// import {
//   createBusinessDescription,
//   updateBusinessDescription,
//   uploadJobHierarchy,
//   submitKpiBasket,
//   submitCriticalTable,
//   submitBusinessDescription,
//   fetchFormStatus,
//   deleteDraft,
// } from "../../Reusable/utils/baseApiService";
import { CRITICALTableData } from "./FormSteps/CriticalTableStep";

interface FormStatus {
  bd_stage: string;
  job_hie_stage: string;
  kpi_basket_stage: string;
  critical_matrix_stage: string;
  job_hierarchy_data?: {
    file_size: string;
    doc_path?: string;
    file_name?: string;
  };
  [key: string]: any;
}

interface BusinessDescriptionFormProps {
  functionL1: string;
  functionL2: string;
  management: string;
}

interface Content {
  suppliers: string;
  outputs: string;
  inputs: string;
  id: number;
  created_at: string;
  updated_at: string;
  status: string;
  bd_stage: string;
  clients: string;
  processes?: string;
  resources?: string;
  objective_or_purpose?: string;
}

const steps = [
  "Business Description",
  "Job Hierarchy",
  "KPI Basket",
  "Critical Matrix",
  "Final Status",
];

const BusinessDescriptionForm: React.FC<BusinessDescriptionFormProps> = ({
  functionL1,
  functionL2,
  management,
}) => {
  const apiService = useApiService();

  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const { id } = useParams();
  const [prevId, setPrevId] = useState<string | undefined>(id);

  const location = useLocation();
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [isLoadingInitialData, setIsLoadingInitialData] = useState(false);

  const loadFormData = async () => {
    if (id && !initialDataLoaded) {
      setIsLoadingInitialData(true);
      try {
        const response = await apiService.fetchBusinessDescriptionDetails(
          Number(id),
        );
        const data = response.data;
        console.log("Form data loaded:", data);

        // Set form data from API response
        setSipocData({
          Clients: data.business_desc_data.clients
            ? data.business_desc_data.clients.split("|")
            : [""],
          outputs: data.business_desc_data.outputs
            ? data.business_desc_data.outputs.split("|")
            : [""],
          inputs: data.business_desc_data.inputs
            ? data.business_desc_data.inputs.split("|")
            : [""],
          suppliers: data.business_desc_data.suppliers
            ? data.business_desc_data.suppliers.split("|")
            : [""],
          processes: data.business_desc_data.processes
            ? data.business_desc_data.processes.split("|")
            : [""],
          resources: data.business_desc_data.resources
            ? data.business_desc_data.resources.split("|")
            : [""],
          objectives: data.business_desc_data.objective_or_purpose
            ? data.business_desc_data.objective_or_purpose.split("|")
            : [""],
        });

        // Set form status from API response
        setFormStatus({
          bd_stage: data.bd_stage || "",
          job_hie_stage: data.job_hie_stage || "",
          kpi_basket_stage: data.kpi_basket_stage || "",
          critical_matrix_stage: data.critical_matrix_stage || "",
        });

        // Set business ID
        setBusinessId(data.id);
        setIsEditMode(true);

        // Set job hierarchy data if available
        console.log("Job Hierarchy Data:", data.job_hierarchy_data);
        setJobHierarchyData(data.job_hierarchy_data || []);

        // Load KPI basket data if available
        if (data.kpiBasketList && data.kpiBasketList.length > 0) {
          const formattedKpiData = data.kpiBasketList.map((kpi: any) => ({
            indicater_name: [kpi.indicator_name || ""],
            what_does_it_measure: [kpi.what_does_it_measure || ""],
            unit_of_measure: [kpi.unit_of_measure || ""],
            what_is_the_calculation_formula: [
              kpi.what_is_the_calculation_formula || "",
            ],
            Check_Items: [kpi.check_items || ""],
            who_is_responsible: [kpi.who_is_responsible || ""],
            Is_it_related_to_any_global_goal: [
              kpi.is_it_related_to_any_global_goal ? "Yes" : "No",
            ],
            Which_goal: [kpi.whichgoal || ""],
            year: [kpi.year?.toString() || ""],
            current_YDT_value: [kpi.current_ytd_value?.toString() || ""],
            previous_FY_year: [kpi.previous_fy_value?.toString() || ""],
            target_value: [kpi.target_value?.toString() || ""],
          }));
          setKpiBasketData([formattedKpiData[0]]); // First item as main form
          setAccordions(formattedKpiData.slice(1)); // Remaining items as accordions
        }

        // Load critical table data if available
        if (data.critical_matrices && data.critical_matrices.length > 0) {
          setCriticalTableData(
            data.critical_matrices.map((matrix: any) => ({
              output: matrix.output || "",
              reputational_external_impact_on:
                matrix.reputational_external_impact_on || 0,
              monthly_financial_impact: matrix.monthly_financial_impact || 0,
              compliance_or_external_regulatory_affairs_impact:
                matrix.compliance_or_external_regulatory_affairs_impact || 0,
              time_to_deliver_the_service:
                matrix.time_to_deliver_the_service || 0,
              sum: matrix.sum || 0,
              critical: matrix.critical || "-",
            })),
          );
        }

        // Determine which step to show based on form status
        if (data.critical_matrix_stage === "Completed") {
          setActiveStep(4); // Final Status
          setIsFinished(true);
        } else if (data.critical_matrix_stage === "Submitted") {
          setActiveStep(3); // Critical Matrix
        } else if (data.kpi_basket_stage === "Submitted") {
          setActiveStep(2); // KPI Basket
        } else if (data.job_hie_stage === "Submitted") {
          setActiveStep(1); // Job Hierarchy
        } else {
          setActiveStep(0); // Business Description
        }

        setInitialDataLoaded(true);
      } catch (error) {
        console.error("Failed to load form data:", error);
      } finally {
        setIsLoadingInitialData(false);
      }
    }
  };

  useEffect(() => {
    // Only run if id changes and is defined
    if (id) {
      console.log("ID changed, loading data for:", id);

      // Reset form state
      setInitialDataLoaded(false);
      setPrevId(id);

      // Load new data
      loadFormData();
    }
  }, [id]); // Only depend on id
  const [isFileSelected, setIsFileSelected] = useState(false);
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [businessId, setBusinessId] = useState<number | null>(null);
  const [selectedUpload, setSelectedUpload] = useState<boolean>(false);
  const [selectedFile, setSelectedFile] = useState<
    Record<string, { file: File | null; metadata: string[] }>
  >({});

  const [sipocData, setSipocData] = useState({
    Clients: [""],
    outputs: [""],
    inputs: [""],
    suppliers: [""],
    processes: [""],
    resources: [""],
    objectives: [""],
  });
  const [kpiBasketData, setKpiBasketData] = useState<KPIBasketData[]>([
    {
      indicater_name: [""],
      what_does_it_measure: [""],
      unit_of_measure: [""],
      what_is_the_calculation_formula: [""],
      Check_Items: [""],
      who_is_responsible: [""],
      Is_it_related_to_any_global_goal: [""],
      Which_goal: [""],
      year: [""],
      current_YDT_value: [""],
      previous_FY_year: [""],
      target_value: [""],
    },
  ]);
  const [accordions, setAccordions] = useState<KPIBasketData[]>([]);

  const [criticalTableData, setCriticalTableData] = useState<
    CRITICALTableData[]
  >([]);
  const [jobHierarchyData, setJobHierarchyData] = useState<any[]>([]);
  const [isFinished, setIsFinished] = useState(false);
  const [formStatus, setFormStatus] = useState<FormStatus>({
    bd_stage: "",
    job_hie_stage: "",
    kpi_basket_stage: "",
    critical_matrix_stage: "",
  });
  const [drafts, setDrafts] = useState<Content[]>([]);
  const [showDrafts, setShowDrafts] = useState(false);
  const [editDraftId, setEditDraftId] = useState<number | null>(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loading1, setLoading1] = useState(false);

  const statusColors: Record<string, string> = {
    Draft: "#FFA500",
    Approved: "#058B2B",
    Reverted: "#FF0000",
    Submitted: "#0000FF",
  };

  const handleDeleteDraft = async (id: number) => {
    try {
      await apiService.deleteDraft(id);
      setOpenDeleteModal(false);
    } catch (error) {
      console.error("Error deleting draft:", error);
    }
  };

  const isFormFilled = () => {
    return Object.values(sipocData).every((fields) =>
      fields.every((field) => field.trim() !== ""),
    );
  };

  const isKpiBasketFilled = () => {
    return (
      kpiBasketData.length > 0 &&
      kpiBasketData.every((kpi) =>
        Object.entries(kpi).every(
          ([key, fields]) =>
            key === "Which_goal" ||
            fields.every(
              (field: { toString: () => string }) =>
                field.toString().trim() !== "",
            ),
        ),
      )
    );
  };

  const isCriticalTableFilled = () => {
    return (
      criticalTableData.length > 0 &&
      criticalTableData.every((row) =>
        Object.values(row).every(
          (value) => value !== null && value !== "" && value !== 0,
        ),
      )
    );
  };

  const handleNext = async () => {
    setLoading(true);
    try {
      switch (activeStep) {
        case 0:
          await handleBusinessDescriptionStep(false);
          break;
        case 1:
          await handleJobHierarchyStep();
          break;
        case 2:
          await handleKpiBasketStep(false);
          break;
        case 3:
          await handleCriticalTableStep(false);
          break;
        default:
          break;
      }

      if (activeStep < steps.length - 1) {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
      }
    } catch (error) {
      console.error("Error during API call:", error);
      alert("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDraft = async () => {
    setLoading1(true);
    try {
      switch (activeStep) {
        case 0:
          await handleBusinessDescriptionStep(true);
          break;
        case 1:
          await handleJobHierarchyStep();
          break;
        case 2:
          await handleKpiBasketStep(true);
          break;
        case 3:
          await handleCriticalTableStep(true);
          break;
        default:
          break;
      }

      if (activeStep === 0) {
        setShowDrafts(true);
      }
    } catch (error) {
      console.error("Error during save draft:", error);
      alert("An error occurred. Please try again.");
    } finally {
      setLoading1(false);
    }
  };

  const handleBusinessDescriptionStep = async (isDraft: boolean) => {
    const data = {
      management,
      function_l1: functionL1,
      function_l2: functionL2,
      clients: sipocData.Clients.join("|"),
      outputs: sipocData.outputs.join("|"),
      inputs: sipocData.inputs.join("|"),
      suppliers: sipocData.suppliers.join("|"),
      processes: sipocData.processes.join("|"),
      resources: sipocData.resources.join("|"),
      objective_or_purpose: sipocData.objectives.join("|"),
    };

    try {
      const response =
        isEditMode || businessId
          ? await apiService.updateBusinessDescription(businessId!, data)
          : await apiService.createBusinessDescription(data);

      if (response.data) {
        setBusinessId(response.data.id);
        setFormStatus(response.data);
      }
    } catch (error) {
      console.error("Error saving data:", error);
    }
  };

  const handleJobHierarchyStep = async () => {
    if (businessId && selectedFile) {
      const fileKey = Object.keys(selectedFile)[0];
      const file = selectedFile[fileKey]?.file;
      if (file && selectedUpload) {
        const formData = new FormData();
        formData.append("file", file);
        try {
          const response = await apiService.uploadJobHierarchy(
            businessId,
            formData,
          );
          setSelectedFile({}); // Clear selected file after upload
          setFormStatus(response.data);
          const response_file =
            await apiService.fetchBusinessDescriptionDetails(
              Number(businessId),
            );
          const data = response_file.data;
          setJobHierarchyData(data.job_hierarchy_data || []);
          console.log("File uploaded successfully!");
        } catch (error) {
          console.error("Failed to upload Job Hierarchy file:", error);
          throw error;
        }
      } else {
        console.error("No file found in selectedFile.");
      }
    }
  };

  const handleKpiBasketStep = async (isDraft = false) => {
    if (!businessId) {
      console.error("No business ID found");
      return;
    }

    setLoading(true);

    try {
      const allKpiData = [...kpiBasketData, ...accordions];
      const filteredKpiData = isDraft
        ? allKpiData
        : allKpiData.filter((kpi) => {
            return Object.values(kpi).some((fieldArray) =>
              fieldArray.some((field: any) => field?.toString().trim() !== ""),
            );
          });

      const formattedKpiData = filteredKpiData.map((item) => ({
        indicator_name: item.indicater_name[0]?.toString().trim() || "",
        what_does_it_measure:
          item.what_does_it_measure[0]?.toString().trim() || "",
        unit_of_measure: item.unit_of_measure[0]?.toString().trim() || "",
        what_is_the_calculation_formula:
          item.what_is_the_calculation_formula[0]?.toString().trim() || "",
        check_items: item.Check_Items[0]?.toString().trim() || "",
        who_is_responsible: item.who_is_responsible[0]?.toString().trim() || "",
        is_it_related_to_any_global_goal:
          item.Is_it_related_to_any_global_goal[0] === "Yes",
        whichgoal: item.Which_goal[0]?.toString().trim() || "",
        year: item.year[0] ? parseInt(item.year[0]) : new Date().getFullYear(),
        current_ytd_value: item.current_YDT_value[0]
          ? parseFloat(item.current_YDT_value[0])
          : 0,
        previous_fy_value: item.previous_FY_year[0]
          ? parseFloat(item.previous_FY_year[0])
          : 0,
        target_value: item.target_value[0]
          ? parseFloat(item.target_value[0])
          : 0,
        is_draft: isDraft,
      }));

      const response = await apiService.submitKpiBasket(
        businessId,
        formattedKpiData,
      );
      setFormStatus(response.data);

      if (!isDraft && response.data.kpi_basket_stage === "Submitted") {
        setAccordions([]);
        setKpiBasketData([
          {
            indicater_name: [""],
            what_does_it_measure: [""],
            unit_of_measure: [""],
            what_is_the_calculation_formula: [""],
            Check_Items: [""],
            who_is_responsible: [""],
            Is_it_related_to_any_global_goal: [""],
            Which_goal: [""],
            year: [""],
            current_YDT_value: [""],
            previous_FY_year: [""],
            target_value: [""],
          },
        ]);
      }

      return response;
    } catch (error) {
      console.error("Error submitting KPI Basket:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleCriticalTableStep = async (isDraft: boolean) => {
    if (businessId) {
      const criticalTableRequestBody = criticalTableData.map((row) => ({
        output: row.output,
        reputational_external_impact_on:
          row.reputational_external_impact_on || 0,
        monthly_financial_impact: row.monthly_financial_impact || 0,
        compliance_or_external_regulatory_affairs_impact:
          row.compliance_or_external_regulatory_affairs_impact || 0,
        time_to_deliver_the_service: row.time_to_deliver_the_service || 0,
        sum: row.sum || 0,
        critical: row.critical || "-",
      }));

      const response = await apiService.submitCriticalTable(
        businessId,
        criticalTableRequestBody,
      );
      setFormStatus(response.data);

      // If this is the final submission (not a draft), submit the entire form
      if (!isDraft) {
        await apiService.submitBusinessDescription(businessId);
        setIsFinished(true);
        setActiveStep(steps.length - 1);
      }
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep((prevActiveStep) => prevActiveStep - 1);
    }
  };

  const handleFinish = async () => {
    setLoading(true);
    try {
      if (activeStep === 3 && businessId) {
        await handleCriticalTableStep(false);
      }
    } catch (error) {
      console.error("Error during API call:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {isLoadingInitialData ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "80vh",
          }}
        >
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ ml: 2 }}>
            Loading form data...
          </Typography>
        </Box>
      ) : (
        <Box>
          <Paper className={Styles.mainForm} elevation={0} sx={{ p: 2, mb: 2 }}>
            <p className={Styles.formNavigaion}>
              <span>{functionL1}</span>{" "}
              <ArrowForwardIosIcon style={{ fontSize: 20 }} />{" "}
              <span>{functionL2}</span>
            </p>

            {activeStep !== 4 && (
              <ProgressBar
                steps={steps}
                activeStep={activeStep}
                formStatus={formStatus}
              />
            )}

            {activeStep === 0 && (
              <SIPOCDiagramStep
                sipocData={sipocData}
                setSipocData={setSipocData}
                isEditMode={editDraftId !== null}
              />
            )}

            {activeStep === 1 && (
              <JobHierarchyUploadStep
                setSelectedFile={setSelectedFile}
                setIsFileSelected={setIsFileSelected}
                selectedFile={selectedFile}
                isEditMode={isEditMode}
                jobHierarchyData={jobHierarchyData}
                setIsLoadingPreview={setIsLoadingPreview}
                setSelectedUpload={setSelectedUpload}
              />
            )}

            {activeStep === 2 && (
              <KPIBasket
                setKpiBasketData={setKpiBasketData}
                kpiBasketData={kpiBasketData}
                isKpiBasketFilled={isKpiBasketFilled()}
                accordions={accordions}
                setAccordions={setAccordions}
              />
            )}

            {activeStep === 3 && (
              <CriticalTableStep
                outputs={sipocData.outputs}
                setCriticalTableData={setCriticalTableData}
                criticalTableData={criticalTableData}
                isCriticalTableFilled={isCriticalTableFilled()}
              />
            )}

            {activeStep === 4 && isFinished && (
              <FinalStatus
                functionL1={functionL1}
                functionL2={functionL2}
                management={management}
              />
            )}
          </Paper>

          <Modal
            open={openDeleteModal}
            onClose={() => setOpenDeleteModal(false)}
            aria-labelledby="delete-confirmation-modal"
            aria-describedby="delete-confirmation-description"
          >
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: 400,
                bgcolor: "background.paper",
                boxShadow: 24,
                p: 4,
                borderRadius: 2,
              }}
            >
              <Typography variant="h6" gutterBottom>
                Confirm Delete
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                Are you sure you want to delete this draft?
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => setOpenDeleteModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => {
                    if (editDraftId) {
                      handleDeleteDraft(editDraftId);
                    }
                  }}
                >
                  Delete
                </Button>
              </Box>
            </Box>
          </Modal>

          {activeStep !== 4 && (
            <FormFooter
              activeStep={activeStep}
              steps={steps}
              handleBack={handleBack}
              handleSaveDraft={handleSaveDraft}
              handleNext={handleNext}
              handleFinish={handleFinish}
              isFormFilled={isFormFilled()}
              isFileSelected={isFileSelected}
              isKpiBasketFilled={isKpiBasketFilled()}
              isCriticalTableFilled={isCriticalTableFilled()}
              loading={loading}
              loading1={loading1}
              formStatus={formStatus}
              accordions={accordions}
              kpiBasketData={kpiBasketData}
            />
          )}
        </Box>
      )}
    </>
  );
};

export default BusinessDescriptionForm;
